import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, apiClient } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wallet, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { isNativePlatform } from '@/config/platform';
import { toast } from 'sonner';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') || 'login';
  const [mode, setMode] = useState(
    initialMode === 'register' || initialMode === 'forgot' || initialMode === 'reset'
      ? initialMode
      : 'login'
  );
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [resetLink, setResetLink] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    passwordConfirm: '',
  });

  const navigate = useNavigate();
  const { login, register, user } = useAuth();
  const nativeApp = isNativePlatform();

  const switchMode = (nextMode, options = {}) => {
    const params = new URLSearchParams();
    params.set('mode', nextMode);
    if (options.token) {
      params.set('token', options.token);
    }
    setMode(nextMode);
    navigate(`/auth?${params.toString()}`, { replace: options.replace ?? true });
  };

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const nextMode = searchParams.get('mode') || 'login';
    if (nextMode === 'register' || nextMode === 'forgot' || nextMode === 'reset') {
      setMode(nextMode);
    } else {
      setMode('login');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
        navigate('/dashboard');
        return;
      }

      if (mode === 'register') {
        if (!formData.name.trim()) {
          toast.error('Vul je naam in');
          setLoading(false);
          return;
        }
        await register(formData.name, formData.email, formData.password);
        navigate('/dashboard');
        return;
      }

      if (mode === 'forgot') {
        const response = await apiClient.post('/auth/forgot-password', { email: formData.email });
        toast.success(response.data?.message || 'Controleer je e-mail voor de volgende stap.');
        if (response.data?.reset_url) {
          setResetLink(response.data.reset_url);
          const url = new URL(response.data.reset_url);
          const token = url.searchParams.get('token');
          if (token) {
            setFormData((prev) => ({ ...prev, password: '', passwordConfirm: '' }));
            switchMode('reset', { token });
          }
        }
        return;
      }

      if (mode === 'reset') {
        const token = searchParams.get('token');
        if (!token) {
          toast.error('Deze resetlink is ongeldig.');
          setLoading(false);
          return;
        }
        if (formData.password.length < 8) {
          toast.error('Gebruik een wachtwoord van minimaal 8 tekens');
          setLoading(false);
          return;
        }
        if (formData.password !== formData.passwordConfirm) {
          toast.error('De wachtwoorden komen niet overeen');
          setLoading(false);
          return;
        }
        await apiClient.post('/auth/reset-password', { token, password: formData.password });
        toast.success('Je wachtwoord is aangepast. Log nu opnieuw in.');
        setFormData((prev) => ({ ...prev, password: '', passwordConfirm: '' }));
        setResetLink('');
        switchMode('login');
        return;
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Er ging iets mis';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex overflow-x-hidden">
      {/* Left Panel - Form */}
      <div className={`flex-1 flex flex-col px-8 py-12 sm:px-12 lg:px-16 ${nativeApp ? 'ios-safe-top ios-safe-bottom justify-start pt-4' : 'justify-center'}`}>
        <div className={`w-full max-w-md mx-auto ${nativeApp ? 'liquid-glass-surface mt-3 rounded-[28px] p-6 sm:p-8' : ''}`}>
          {!nativeApp && (
            <button
              onClick={() => navigate('/')}
              className="mb-8 flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Terug naar home</span>
            </button>
          )}

          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
              <Wallet className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl">budgettoppers</h1>
              <p className="text-sm text-muted-foreground">
                {mode === 'login' && 'Welkom terug!'}
                {mode === 'register' && 'Maak je account aan'}
                {mode === 'forgot' && 'Herstel je wachtwoord'}
                {mode === 'reset' && 'Kies een nieuw wachtwoord'}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {mode === 'register' && (
              <div className="space-y-2">
                <Label htmlFor="name">Naam</Label>
                <Input
                  id="name"
                  data-testid="auth-name-input"
                  type="text"
                  autoComplete="name"
                  placeholder="Je volledige naam"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-12"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">E-mailadres</Label>
              <Input
                id="email"
                data-testid="auth-email-input"
                type="email"
                autoComplete="username"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="naam@voorbeeld.nl"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-12"
                required
              />
            </div>

            {mode !== 'forgot' && (
              <div className="space-y-2">
                <Label htmlFor="password">{mode === 'reset' ? 'Nieuw wachtwoord' : 'Wachtwoord'}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="auth-password-input"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={mode === 'reset' ? 'new-password' : 'current-password'}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    passwordRules={mode === 'reset' ? 'minlength: 8;' : undefined}
                    enterKeyHint={mode === 'login' ? 'go' : 'next'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="h-12 pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}

            {mode === 'reset' && (
              <div className="space-y-2">
                <Label htmlFor="passwordConfirm">Herhaal wachtwoord</Label>
                <div className="relative">
                  <Input
                    id="passwordConfirm"
                    type={showPasswordConfirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    passwordRules="minlength: 8;"
                    enterKeyHint="go"
                    placeholder="••••••••"
                    value={formData.passwordConfirm}
                    onChange={(e) => setFormData({ ...formData, passwordConfirm: e.target.value })}
                    className="h-12 pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPasswordConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}

            {mode === 'forgot' && resetLink && (
              <div className="rounded-lg border border-primary/15 bg-primary/5 p-4 text-sm">
                Herstelstap gestart. In deze omgeving gaat de reset direct door naar het nieuwe wachtwoord.
              </div>
            )}

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-lg"
            >
              {loading ? 'Even geduld...' : (
                mode === 'login'
                  ? 'Inloggen'
                  : mode === 'register'
                    ? 'Account aanmaken'
                    : mode === 'forgot'
                      ? 'Herstel starten'
                      : 'Nieuw wachtwoord opslaan'
              )}
            </Button>
          </form>

          {mode === 'login' && (
            <>
              <div className="mt-4 text-right">
                <button
                  type="button"
                  onClick={() => switchMode('forgot')}
                  className="text-sm text-primary font-medium hover:underline"
                >
                  Wachtwoord vergeten?
                </button>
              </div>
              <p className="text-center mt-6 text-muted-foreground">
                Nog geen account?{' '}
                <button
                  data-testid="auth-toggle-btn"
                  onClick={() => switchMode('register')}
                  className="text-primary font-medium hover:underline"
                >
                  Registreer hier
                </button>
              </p>
            </>
          )}

          {mode === 'register' && (
            <p className="text-center mt-6 text-muted-foreground">
              Al een account?{' '}
              <button
                data-testid="auth-toggle-btn"
                onClick={() => switchMode('login')}
                className="text-primary font-medium hover:underline"
              >
                Log hier in
              </button>
            </p>
          )}

          {(mode === 'forgot' || mode === 'reset') && (
            <p className="text-center mt-6 text-muted-foreground">
              Terug naar{' '}
              <button
                type="button"
                onClick={() => {
                  setResetLink('');
                  switchMode('login');
                }}
                className="text-primary font-medium hover:underline"
              >
                inloggen
              </button>
            </p>
          )}
        </div>
      </div>

      {/* Right Panel - Visual */}
      <div className="hidden lg:flex flex-1 bg-primary items-center justify-center p-12">
        <div className="max-w-lg text-center text-white">
          <h2 className="font-heading text-4xl font-bold mb-6">
            Krijg grip op je financiën
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Meer dan 5.000 Nederlanders gebruiken budgettoppers om gemiddeld €180 per maand te besparen.
          </p>
          <div className="grid grid-cols-3 gap-6">
            <div className="p-4 rounded-xl bg-white/10">
              <p className="text-3xl font-bold">€180</p>
              <p className="text-sm text-white/70">Gem. besparing/mnd</p>
            </div>
            <div className="p-4 rounded-xl bg-white/10">
              <p className="text-3xl font-bold">5K+</p>
              <p className="text-sm text-white/70">Gebruikers</p>
            </div>
            <div className="p-4 rounded-xl bg-white/10">
              <p className="text-3xl font-bold">4.8</p>
              <p className="text-sm text-white/70">App Store</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
